type JsonRecord = Record<string, unknown>;

export type MercadoPagoSubscriptionStatus =
  | 'active'
  | 'authorized'
  | 'cancelled'
  | 'failed'
  | 'paused'
  | 'pending'
  | 'rejected'
  | (string & {});

export interface MercadoPagoSubscription extends JsonRecord {
  id?: string;
  businessId?: string;
  checkoutUrl?: string;
  currentPeriodEnd?: string | null;
  currentPeriodStart?: string | null;
  estado?: MercadoPagoSubscriptionStatus;
  amountCop?: number;
  latestPaymentStatus?: string | null;
  latestProviderPaymentStatus?: string | null;
  monthlyAmountCop?: number;
  pendingOperationStatus?: string | null;
  planCode?: string | null;
  professionalCount?: number;
  providerStatus?: string | null;
  status?: MercadoPagoSubscriptionStatus;
  subscriptionStatus?: string | null;
}

interface MercadoPagoAuthenticatedInput {
  authToken: string;
  businessId: string;
}

export interface StartMercadoPagoSubscriptionInput
  extends MercadoPagoAuthenticatedInput {
  idempotencyKey: string;
  payerEmail: string;
  professionalCount: number;
}

export type GetCurrentMercadoPagoSubscriptionInput =
  MercadoPagoAuthenticatedInput;

export interface CancelMercadoPagoSubscriptionInput
  extends MercadoPagoAuthenticatedInput {
  idempotencyKey?: string;
}

export interface UpdateProfessionalQuantityInput
  extends MercadoPagoAuthenticatedInput {
  idempotencyKey?: string;
  professionalCount: number;
}

export interface RetryMercadoPagoSubscriptionInput
  extends MercadoPagoAuthenticatedInput {
  idempotencyKey: string;
  payerEmail?: string;
  professionalCount?: number;
}

export interface UpdateMercadoPagoPaymentMethodInput
  extends MercadoPagoAuthenticatedInput {
  cardTokenId: string;
  idempotencyKey: string;
}

export interface MercadoPagoSubscriptionResult {
  checkoutUrl: string | null;
  subscription: MercadoPagoSubscription | null;
}

export class MercadoPagoBillingError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status = 0, code: string | null = null) {
    super(message);
    this.name = 'MercadoPagoBillingError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function getSubscriptionEndpoint(): string {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim();

  if (!supabaseUrl) {
    throw new MercadoPagoBillingError(
      'No se pudo conectar con el servicio de suscripciones. Intenta de nuevo más tarde.'
    );
  }

  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/mercado-pago-subscription`;
}

function getHeaders(
  authToken: string,
  requestId: string
): Record<string, string> {
  const apiKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim();

  if (!apiKey) {
    throw new MercadoPagoBillingError(
      'No se pudo conectar con el servicio de suscripciones. Intenta de nuevo más tarde.'
    );
  }

  if (!authToken.trim()) {
    throw new MercadoPagoBillingError(
      'Tu verificación venció. Vuelve a ingresar para continuar con la suscripción.',
      401
    );
  }

  return {
    apikey: apiKey,
    Authorization: `Bearer ${authToken.trim()}`,
    'Content-Type': 'application/json',
    'x-request-id': requestId,
  };
}

function createBillingRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `billing-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function logBillingClientEvent(
  level: 'info' | 'error',
  event: string,
  details: JsonRecord
): void {
  console[level]('[mercado-pago-client]', {
    timestamp: new Date().toISOString(),
    event,
    ...details,
  });
}

function getErrorMessage(payload: unknown): string | null {
  if (!isRecord(payload)) return null;

  for (const key of ['message', 'details', 'error_description']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }

  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error.trim();
  }

  if (isRecord(payload.error)) {
    const nestedMessage = payload.error.message;
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
      return nestedMessage.trim();
    }
  }

  return null;
}

function getErrorCode(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  if (typeof payload.code === 'string' && payload.code.trim()) {
    return payload.code.trim();
  }
  if (isRecord(payload.error) && typeof payload.error.code === 'string') {
    return payload.error.code.trim() || null;
  }
  return null;
}

function normalizeSubscription(value: unknown): MercadoPagoSubscription | null {
  if (!isRecord(value)) return null;

  const normalized: MercadoPagoSubscription = { ...value };
  const businessId = value.businessId ?? value.business_id ?? value.negocio_id;
  const professionalCount =
    value.professionalCount ??
    value.professional_count ??
    value.professionals_contracted ??
    value.cantidad_profesionales;
  const amountCop = value.amountCop ?? value.amount_cop ?? value.monto_esperado_cop;
  const monthlyAmountCop =
    value.monthlyAmountCop ?? value.monthly_amount_cop ?? value.monto_mensual_cop;
  const currentPeriodStart =
    value.currentPeriodStart ??
    value.current_period_start ??
    value.period_start ??
    value.inicio_periodo;
  const currentPeriodEnd =
    value.currentPeriodEnd ??
    value.current_period_end ??
    value.period_end ??
    value.fin_periodo;
  const pendingOperationStatus =
    value.pendingOperationStatus ?? value.pending_operation_status;
  const latestPaymentStatus =
    value.latestPaymentStatus ?? value.latest_payment_status;
  const latestProviderPaymentStatus =
    value.latestProviderPaymentStatus ?? value.latest_provider_payment_status;
  const planCode = value.planCode ?? value.plan_code;
  const providerStatus = value.providerStatus ?? value.provider_status;
  const subscriptionStatus =
    value.subscriptionStatus ?? value.subscription_status;

  if (typeof businessId === 'string') normalized.businessId = businessId;
  if (typeof professionalCount === 'number') {
    normalized.professionalCount = professionalCount;
  }
  if (typeof amountCop === 'number') normalized.amountCop = amountCop;
  if (typeof monthlyAmountCop === 'number') {
    normalized.monthlyAmountCop = monthlyAmountCop;
  }
  if (typeof currentPeriodStart === 'string' || currentPeriodStart === null) {
    normalized.currentPeriodStart = currentPeriodStart;
  }
  if (typeof currentPeriodEnd === 'string' || currentPeriodEnd === null) {
    normalized.currentPeriodEnd = currentPeriodEnd;
  }
  if (
    typeof pendingOperationStatus === 'string' ||
    pendingOperationStatus === null
  ) {
    normalized.pendingOperationStatus = pendingOperationStatus;
  }
  if (typeof latestPaymentStatus === 'string' || latestPaymentStatus === null) {
    normalized.latestPaymentStatus = latestPaymentStatus;
  }
  if (
    typeof latestProviderPaymentStatus === 'string' ||
    latestProviderPaymentStatus === null
  ) {
    normalized.latestProviderPaymentStatus = latestProviderPaymentStatus;
  }
  if (typeof planCode === 'string' || planCode === null) {
    normalized.planCode = planCode;
  }
  if (typeof providerStatus === 'string' || providerStatus === null) {
    normalized.providerStatus = providerStatus;
  }
  if (
    typeof subscriptionStatus === 'string' ||
    subscriptionStatus === null
  ) {
    normalized.subscriptionStatus = subscriptionStatus;
  }

  return normalized;
}

function normalizeResult(payload: unknown): MercadoPagoSubscriptionResult {
  if (!isRecord(payload)) {
    return { checkoutUrl: null, subscription: null };
  }

  const checkoutUrl =
    payload.checkoutUrl ?? payload.checkout_url ?? payload.initPoint ?? payload.init_point;
  const subscriptionCandidate = payload.subscription ?? payload.suscripcion ?? payload;

  return {
    checkoutUrl:
      typeof checkoutUrl === 'string' && checkoutUrl.trim()
        ? checkoutUrl.trim()
        : null,
    subscription: normalizeSubscription(subscriptionCandidate),
  };
}

async function callSubscriptionAction(
  authToken: string,
  body: JsonRecord
): Promise<MercadoPagoSubscriptionResult> {
  const requestId = createBillingRequestId();
  const startedAt = Date.now();
  const action = typeof body.action === 'string' ? body.action : 'unknown';
  const businessId = typeof body.businessId === 'string' ? body.businessId : null;
  let response: Response;

  logBillingClientEvent('info', 'subscription_request_started', {
    requestId,
    action,
    businessId,
  });

  try {
    response = await fetch(getSubscriptionEndpoint(), {
      method: 'POST',
      headers: getHeaders(authToken, requestId),
      body: JSON.stringify(body),
      cache: 'no-store',
    });
  } catch (error) {
    logBillingClientEvent(
      'error',
      error instanceof MercadoPagoBillingError
        ? 'subscription_request_setup_failed'
        : 'subscription_request_network_failed',
      {
        requestId,
        action,
        businessId,
        durationMs: Date.now() - startedAt,
        errorName: error instanceof Error ? error.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message.slice(0, 300) : null,
      }
    );
    if (error instanceof MercadoPagoBillingError) throw error;
    throw new MercadoPagoBillingError(
      'No pudimos comunicarnos con Mercado Pago. Revisa tu conexión e intenta de nuevo.'
    );
  }

  const payload = (await response.json().catch(() => null)) as unknown;
  const serverRequestId = isRecord(payload) && typeof payload.requestId === 'string'
    ? payload.requestId
    : null;

  if (!response.ok) {
    logBillingClientEvent('error', 'subscription_request_failed', {
      requestId,
      serverRequestId,
      action,
      businessId,
      status: response.status,
      errorCode: getErrorCode(payload),
      durationMs: Date.now() - startedAt,
    });
    throw new MercadoPagoBillingError(
      getErrorMessage(payload) ??
        'No fue posible procesar la suscripción en este momento.',
      response.status,
      getErrorCode(payload)
    );
  }

  logBillingClientEvent('info', 'subscription_request_succeeded', {
    requestId,
    serverRequestId,
    action,
    businessId,
    status: response.status,
    durationMs: Date.now() - startedAt,
  });

  return normalizeResult(payload);
}

export function startMercadoPagoSubscription(
  input: StartMercadoPagoSubscriptionInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'start',
    businessId: input.businessId,
    idempotencyKey: input.idempotencyKey,
    payerEmail: input.payerEmail.trim(),
    professionalCount: input.professionalCount,
  });
}

export function getCurrentMercadoPagoSubscription(
  input: GetCurrentMercadoPagoSubscriptionInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'current',
    businessId: input.businessId,
  });
}

export function cancelMercadoPagoSubscription(
  input: CancelMercadoPagoSubscriptionInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'cancel',
    businessId: input.businessId,
    ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
  });
}

export function updateProfessionalQuantity(
  input: UpdateProfessionalQuantityInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'update-professionals',
    businessId: input.businessId,
    ...(input.idempotencyKey
      ? { idempotencyKey: input.idempotencyKey }
      : {}),
    professionalCount: input.professionalCount,
  });
}

export function retryMercadoPagoSubscription(
  input: RetryMercadoPagoSubscriptionInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'retry',
    businessId: input.businessId,
    idempotencyKey: input.idempotencyKey,
    ...(input.payerEmail ? { payerEmail: input.payerEmail.trim() } : {}),
    ...(typeof input.professionalCount === 'number'
      ? { professionalCount: input.professionalCount }
      : {}),
  });
}

export function updateMercadoPagoPaymentMethod(
  input: UpdateMercadoPagoPaymentMethodInput
): Promise<MercadoPagoSubscriptionResult> {
  return callSubscriptionAction(input.authToken, {
    action: 'update-payment-method',
    businessId: input.businessId,
    cardTokenId: input.cardTokenId,
    idempotencyKey: input.idempotencyKey,
  });
}

export function getMercadoPagoSubscriptionStatus(
  subscription: MercadoPagoSubscription | null | undefined
): string {
  if (!subscription) return '';

  const directStatus = subscription.status ?? subscription.estado;
  if (typeof directStatus === 'string' && directStatus.trim()) {
    return directStatus.trim().toLowerCase();
  }

  const planCode = subscription.planCode ?? subscription.plan_code;
  const hasProfessionalEntitlement =
    typeof planCode === 'string' &&
    planCode.trim().toUpperCase() === 'PROFESIONAL';
  const latestPaymentStatus =
    subscription.latestPaymentStatus ?? subscription.latest_payment_status;
  if (typeof latestPaymentStatus === 'string' && latestPaymentStatus.trim()) {
    const normalizedPaymentStatus = latestPaymentStatus.trim().toLowerCase();
    if (
      (normalizedPaymentStatus === 'aprobado' ||
        normalizedPaymentStatus === 'approved') &&
      !hasProfessionalEntitlement
    ) {
      return 'pending';
    }
    return normalizedPaymentStatus;
  }

  const latestProviderPaymentStatus =
    subscription.latestProviderPaymentStatus ??
    subscription.latest_provider_payment_status;
  if (
    typeof latestProviderPaymentStatus === 'string' &&
    latestProviderPaymentStatus.trim()
  ) {
    const normalizedProviderPaymentStatus =
      latestProviderPaymentStatus.trim().toLowerCase();
    if (
      (normalizedProviderPaymentStatus === 'authorized' ||
        normalizedProviderPaymentStatus === 'approved') &&
      !hasProfessionalEntitlement
    ) {
      return 'pending';
    }
    return normalizedProviderPaymentStatus;
  }

  const pendingOperationStatus =
    subscription.pendingOperationStatus ?? subscription.pending_operation_status;
  if (
    typeof pendingOperationStatus === 'string' &&
    pendingOperationStatus.trim()
  ) {
    return pendingOperationStatus.trim().toLowerCase();
  }

  const providerStatus =
    subscription.providerStatus ??
    subscription.provider_status ??
    subscription.payment_status ??
    subscription.preapproval_status;
  if (typeof providerStatus === 'string' && providerStatus.trim()) {
    return providerStatus.trim().toLowerCase();
  }

  const subscriptionStatus =
    subscription.subscriptionStatus ?? subscription.subscription_status;
  const value =
    hasProfessionalEntitlement
      ? subscriptionStatus
      : null;

  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
